import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Location } from 'history'

export interface LeaveGuard {
    asking: boolean
    stay: () => void
    leave: () => void
    allow: (navigate: () => void) => void
}

export function useLeaveGuard(when: boolean): LeaveGuard {
    const history = useHistory()
    const [target, setTarget] = useState<Location | null>(null)
    const bypass = useRef(false)

    useEffect(() => {
        if (!when) {
            return undefined
        }
        return history.block((location) => {
            if (bypass.current) {
                return undefined
            }
            setTarget(location)
            return false
        })
    }, [when, history])

    const allow = useCallback((navigate: () => void) => {
        bypass.current = true
        try {
            navigate()
        } finally {
            bypass.current = false
        }
    }, [])

    const stay = useCallback(() => setTarget(null), [])

    const leave = useCallback(() => {
        if (target) {
            setTarget(null)
            allow(() => history.push(target))
        }
    }, [target, allow, history])

    return { asking: !!target, stay, leave, allow }
}
